import React, { useState } from 'react';
import { Row, Col, Card, Table, Form, Offcanvas, Dropdown } from 'react-bootstrap';
import ReactApexChart from 'react-apexcharts';
import Select from 'react-select';
import './MyQCView.css';

const MyQCView = () => {
  const [selectedProject, setSelectedProject] = useState({ value: 'all', label: 'All Projects' });
  const [showActivityOffcanvas, setShowActivityOffcanvas] = useState(false);
  const [sortBy, setSortBy] = useState('newest');
  const [currentStatus, setCurrentStatus] = useState('Active');
  const [currentPriority, setCurrentPriority] = useState('High');
  const currentUser = 'John Doe';

  // Status and Priority options
  const statusOptions = ['Active', 'On Hold', 'Completed', 'Cancelled'];
  const priorityOptions = ['Low', 'Medium', 'High', 'Critical'];

  // Helper functions
  const getStatusColor = (status) => {
    switch (status) {
      case 'Active': return 'primary';
      case 'On Hold': return 'warning';
      case 'Completed': return 'success';
      case 'Cancelled': return 'danger';
      default: return 'secondary';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'Critical': return 'danger';
      case 'High': return 'warning';
      case 'Medium': return 'info';
      case 'Low': return 'secondary';
      default: return 'secondary';
    }
  };

  // Mock projects data - formatted for react-select
  const projects = [
    { value: 'all', label: 'All Projects' },
    { value: 'pms', label: 'Project Management System' },
    { value: 'mobile', label: 'Mobile App' },
    { value: 'api', label: 'API Gateway' },
    { value: 'website', label: 'Corporate Website' }
  ];

  // SECTION 1: Assigned to Me (Unresolved) - MantisBT data
  const assignedToMe = [
    {
      id: 'BUG-1234',
      title: 'Login page not responsive on mobile devices',
      project: 'Mobile App',
      priority: 'High',
      severity: 'Major',
      status: 'In Progress'
    },
    {
      id: 'BUG-1256',
      title: 'API timeout on large data requests',
      project: 'API Gateway',
      priority: 'Critical',
      severity: 'Critical',
      status: 'New'
    },
    {
      id: 'BUG-1289',
      title: 'Dashboard charts not loading properly',
      project: 'Project Management System',
      priority: 'High',
      severity: 'Major',
      status: 'In Progress'
    },
    {
      id: 'BUG-1301',
      title: 'Email notifications delayed by 5+ minutes',
      project: 'Project Management System',
      priority: 'Medium',
      severity: 'Minor',
      status: 'Acknowledged'
    }
  ];

  // SECTION 2: Unassigned issues
  const unassigned = [
    {
      id: 'BUG-1312',
      title: 'Footer links broken on contact page',
      project: 'Corporate Website',
      priority: 'Low',
      severity: 'Minor',
      status: 'New'
    },
    {
      id: 'BUG-1315',
      title: 'Search functionality returns incorrect results',
      project: 'Corporate Website',
      priority: 'Medium',
      severity: 'Major',
      status: 'New'
    }
  ];

  // SECTION 3: Reported by Me
  const reportedByMe = [
    {
      id: 'BUG-1245',
      title: 'User profile picture upload fails for PNG files',
      project: 'Mobile App',
      status: 'Resolved',
      lastUpdated: '2024-01-18 14:30'
    },
    {
      id: 'BUG-1267',
      title: 'Task completion notification not sent to team members',
      project: 'Project Management System',
      status: 'In Progress',
      lastUpdated: '2024-01-19 09:15'
    },
    {
      id: 'BUG-1290',
      title: 'Export to PDF missing last page of data',
      project: 'Project Management System',
      status: 'Acknowledged',
      lastUpdated: '2024-01-19 16:45'
    }
  ];

  // SECTION 4: Resolved issues
  const resolved = [
    {
      id: 'BUG-1198',
      title: 'Calendar view showing incorrect dates for recurring events',
      resolution: 'Fixed',
      resolvedBy: 'Jane Smith',
      resolvedDate: '2024-01-18'
    },
    {
      id: 'BUG-1205',
      title: 'File upload size limit too restrictive (5MB → 50MB)',
      resolution: 'Fixed',
      resolvedBy: 'Mike Johnson',
      resolvedDate: '2024-01-17'
    },
    {
      id: 'BUG-1223',
      title: 'Timezone conversion error in monthly reports',
      resolution: 'Fixed',
      resolvedBy: 'Sarah Williams',
      resolvedDate: '2024-01-16'
    }
  ];

  // SECTION 5: Recently Modified (Last 30 Days)
  const recentlyModified = [
    {
      id: 'BUG-1289',
      changeSummary: 'Status changed: New → In Progress',
      updatedBy: 'John Doe',
      date: '2024-01-19',
      time: '11:20'
    },
    {
      id: 'BUG-1301',
      changeSummary: 'Priority changed: Low → Medium, assigned to John Doe',
      updatedBy: 'Admin User',
      date: '2024-01-19',
      time: '10:05'
    },
    {
      id: 'BUG-1267',
      changeSummary: 'New comment added by Jane Smith',
      updatedBy: 'Jane Smith',
      date: '2024-01-19',
      time: '09:15'
    },
    {
      id: 'BUG-1256',
      changeSummary: 'Severity changed: Major → Critical',
      updatedBy: 'Tech Lead',
      date: '2024-01-18',
      time: '16:40'
    }
  ];

  // SECTION 6: Monitored by Me
  const monitored = [
    {
      id: 'BUG-1178',
      title: 'Performance degradation on production server during peak hours',
      project: 'API Gateway',
      status: 'In Progress'
    },
    {
      id: 'BUG-1189',
      title: 'Database migration script failing on staging environment',
      project: 'Project Management System',
      status: 'Acknowledged'
    }
  ];

  // Activity Timeline data
  const timeline = [
    {
      action: 'Created bug report',
      issue: 'BUG-1315',
      user: 'Michael Chen',
      time: '5 minutes ago',
      type: 'create'
    },
    {
      action: 'Status changed to In Progress',
      issue: 'BUG-1289',
      user: 'John Doe',
      time: '32 minutes ago',
      type: 'status'
    },
    {
      action: 'Assigned to John Doe',
      issue: 'BUG-1301',
      user: 'Admin User',
      time: '1 hour ago',
      type: 'assign'
    },
    {
      action: 'Added comment',
      issue: 'BUG-1267',
      user: 'Jane Smith',
      time: '2 hours ago',
      type: 'comment'
    },
    {
      action: 'Resolved as Fixed',
      issue: 'BUG-1198',
      user: 'Jane Smith',
      time: '1 day ago',
      type: 'resolve'
    },
    {
      action: 'Updated severity to Critical',
      issue: 'BUG-1256',
      user: 'Tech Lead',
      time: '1 day ago',
      type: 'status'
    }
  ];

  // Helper functions
  const getSelectedProjectName = () => {
    return selectedProject ? selectedProject.label : 'All Projects';
  };

  const getPriorityTextColor = (priority) => {
    switch (priority?.toLowerCase()) {
      case 'critical': return 'text-danger';
      case 'high': return 'text-warning';
      case 'medium': return 'text-primary';
      case 'low': return 'text-secondary';
      default: return 'text-muted';
    }
  };

  const getStatusTextColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'new': return 'text-primary';
      case 'in progress': return 'text-warning';
      case 'acknowledged': return 'text-warning';
      case 'resolved': return 'text-success';
      case 'reopened': return 'text-danger';
      default: return 'text-muted';
    }
  };

  const getTimelineIcon = (type) => {
    switch (type) {
      case 'create': return 'fa-plus-circle text-success';
      case 'status': return 'fa-edit text-primary';
      case 'assign': return 'fa-user-plus text-info';
      case 'comment': return 'fa-comment text-warning';
      case 'resolve': return 'fa-check-circle text-success';
      default: return 'fa-circle text-muted';
    }
  };

  const sortActivityData = (data) => {
    return [...data].sort((a, b) => {
      const dateA = new Date(a.timestamp || a.time);
      const dateB = new Date(b.timestamp || b.time);
      return sortBy === 'newest' ? dateB - dateA : dateA - dateB;
    });
  };

  // KPI Summary Data
  const kpiMetrics = {
    totalItems: 18,
    assignedToMe: 4,
    unassigned: 2,
    resolvedLast7Days: 5,
    pendingApproval: 3,
    avgTimeToResolve: '2.3 days',
    overdueItems: 2
  };

  // Chart Data - Issues Status Distribution
  const issuesStatusDistribution = [
    { status: 'Assigned', count: 5, color: '#3B82F6', icon: 'fa-user-check' },
    { status: 'Feedback', count: 3, color: '#F59E0B', icon: 'fa-comments' },
    { status: 'Closed', count: 8, color: '#6B7280', icon: 'fa-times-circle' },
    { status: 'Resolved', count: 6, color: '#10B981', icon: 'fa-check-circle' },
    { status: 'Confirmed', count: 4, color: '#8B5CF6', icon: 'fa-clipboard-check' },
    { status: 'Acknowledged', count: 2, color: '#06B6D4', icon: 'fa-eye' }
  ];

  // ApexCharts Configuration
  const chartOptions = {
    chart: {
      type: 'donut',
      height: 350,
      fontFamily: 'inherit',
    },
    labels: issuesStatusDistribution.map(item => item.status),
    colors: issuesStatusDistribution.map(item => item.color),
    dataLabels: {
      enabled: true,
      style: {
        fontSize: '14px',
        fontWeight: 'bold',
        colors: ['#fff']
      },
      dropShadow: {
        enabled: false
      }
    },
    plotOptions: {
      pie: {
        donut: {
          size: '70%',
          labels: {
            show: true,
            name: {
              show: true,
              fontSize: '16px',
              fontWeight: 600,
              color: '#1f2937'
            },
            value: {
              show: true,
              fontSize: '28px',
              fontWeight: 700,
              color: '#1f2937',
              formatter: function (val) {
                return val
              }
            },
            total: {
              show: true,
              label: 'Total Issues',
              fontSize: '14px',
              fontWeight: 500,
              color: '#6b7280',
              formatter: function (w) {
                return w.globals.seriesTotals.reduce((a, b) => a + b, 0)
              }
            }
          }
        }
      }
    },
    legend: {
      show: false
    },
    stroke: {
      width: 0
    },
    tooltip: {
      enabled: true,
      y: {
        formatter: function(val) {
          return val + " issues"
        }
      }
    }
  };

  const chartSeries = issuesStatusDistribution.map(item => item.count);

  return (
    <div className="my-qc-view">
      {/* QC Overview Card */}
      <div className='c-card-1 mb-4'>
        <div className='c-card-header-1'>
          <div className="d-flex justify-content-between align-items-center w-100">
            <div>Overview</div>
            <div className="d-flex gap-2 align-items-center">
              {/* Status Quick Edit Dropdown */}
              <Dropdown className="c-dropdown-quick-edit">
                <Dropdown.Toggle 
                  as="span"
                  className={`d-inline-block px-3 py-1 rounded-pill fw-semibold text-sm-plus modern-badge badge-${getStatusColor(currentStatus)} cursor-pointer`}
                >
                  {currentStatus}
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  {statusOptions.map((status) => (
                    <Dropdown.Item 
                      key={status}
                      active={status === currentStatus}
                      onClick={() => setCurrentStatus(status)}
                    >
                      <span 
                        className="badge-indicator" 
                        style={{ backgroundColor: `var(--bs-${getStatusColor(status)})` }}
                      ></span>
                      {status}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>

              {/* Priority Quick Edit Dropdown */}
              <Dropdown className="c-dropdown-quick-edit">
                <Dropdown.Toggle 
                  as="span"
                  className={`d-inline-block px-3 py-1 rounded-pill fw-semibold text-sm-plus modern-badge badge-${getPriorityColor(currentPriority)} cursor-pointer`}
                >
                  {currentPriority} Priority
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  {priorityOptions.map((priority) => (
                    <Dropdown.Item 
                      key={priority}
                      active={priority === currentPriority}
                      onClick={() => setCurrentPriority(priority)}
                    >
                      <span 
                        className="badge-indicator" 
                        style={{ backgroundColor: `var(--bs-${getPriorityColor(priority)})` }}
                      ></span>
                      {priority} Priority
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>

              <Select
                value={selectedProject}
                onChange={setSelectedProject}
                options={projects}
                className="react-select-container project-select"
                classNamePrefix="react-select"
                placeholder=""
                isClearable={false}
              />
              <button 
                className="btn btn-primary btn-sm"
                onClick={() => setShowActivityOffcanvas(true)}
              >
                Activity
              </button>
            </div>
          </div>
        </div>
        <div className='c-card-body-1'>
          <Row className="g-4">
            <Col md={3} sm={6}>
              <div className="text-uppercase text-secondary fw-medium info-label letter-spacing-wide">Project Name</div>
              <div className="text-dark fw-semibold info-value">
                {selectedProject === 'all' ? 'All Projects' : projects.find(p => p.id === selectedProject)?.name || 'Project Management System'}
              </div>
            </Col>
            <Col md={3} sm={6}>
              <div className="text-uppercase text-secondary fw-medium info-label letter-spacing-wide">Project Code</div>
              <div className="text-dark fw-semibold info-value">PRJ-2024-001</div>
            </Col>
            <Col md={3} sm={6}>
              <div className="text-uppercase text-secondary fw-medium info-label letter-spacing-wide">Client</div>
              <div className="text-dark fw-semibold info-value">Netclues Technologies</div>
            </Col>
            <Col md={3} sm={6}>
              <div className="text-uppercase text-secondary fw-medium info-label letter-spacing-wide">QC Manager</div>
              <div className="text-dark fw-semibold info-value">John Doe</div>
            </Col>
            <Col md={3} sm={6}>
              <div className="text-uppercase text-secondary fw-medium info-label letter-spacing-wide">Total QC Items</div>
              <div className="text-dark fw-semibold info-value">{kpiMetrics.totalItems}</div>
            </Col>
            <Col md={3} sm={6}>
              <div className="text-uppercase text-secondary fw-medium info-label letter-spacing-wide">Assigned to Me</div>
              <div className="text-dark fw-semibold info-value">{kpiMetrics.assignedToMe}</div>
            </Col>
            <Col md={3} sm={6}>
              <div className="text-uppercase text-secondary fw-medium info-label letter-spacing-wide">Unassigned Items</div>
              <div className="text-dark fw-semibold info-value">{kpiMetrics.unassigned}</div>
            </Col>
            <Col md={3} sm={6}>
              <div className="text-uppercase text-secondary fw-medium info-label letter-spacing-wide">Resolved (7 Days)</div>
              <div className="text-dark fw-semibold info-value">{kpiMetrics.resolvedLast7Days}</div>
            </Col>
            <Col md={3} sm={6}>
              <div className="text-uppercase text-secondary fw-medium info-label letter-spacing-wide">Pending Approval</div>
              <div className="text-dark fw-semibold info-value">{kpiMetrics.pendingApproval}</div>
            </Col>
            <Col md={3} sm={6}>
              <div className="text-uppercase text-secondary fw-medium info-label letter-spacing-wide">Avg Time to Resolve</div>
              <div className="text-dark fw-semibold info-value">{kpiMetrics.avgTimeToResolve}</div>
            </Col>
            <Col md={3} sm={6}>
              <div className="text-uppercase text-secondary fw-medium info-label letter-spacing-wide">Overdue Items</div>
              <div className="text-dark fw-semibold info-value text-danger">{kpiMetrics.overdueItems}</div>
            </Col>
            <Col md={3} sm={6}>
              <div className="text-uppercase text-secondary fw-medium info-label letter-spacing-wide">Success Rate</div>
              <div className="text-dark fw-semibold info-value">85%</div>
            </Col>
          </Row>
        </div>
      </div>

      {/* Issues Status Distribution Chart */}
      <Row className="mb-4">
        <Col lg={12}>
          <Card className="c-card-1">
            <Card.Header className="c-card-header-1">
              <i className="fas fa-chart-pie me-2"></i>
              Issues Status Distribution
            </Card.Header>
            <Card.Body className="c-card-body-1">
              <Row className="align-items-center">
                {/* ApexCharts Donut Chart */}
                <Col lg={5} md={12} className="text-center mb-4 mb-lg-0">
                  <div className="apex-chart-wrapper">
                    <ReactApexChart
                      options={chartOptions}
                      series={chartSeries}
                      type="donut"
                      height={350}
                    />
                  </div>
                </Col>

                {/* Legend */}
                <Col lg={7} md={12}>
                  <Row className="g-3">
                    {issuesStatusDistribution.map((item, index) => (
                      <Col lg={6} md={6} sm={6} key={index}>
                        <div className="status-legend-item">
                          <div className="d-flex align-items-center mb-2">
                            <div 
                              className="legend-icon me-2"
                              style={{ backgroundColor: item.color }}
                            >
                              <i className={`fas ${item.icon}`}></i>
                            </div>
                            <div className="flex-grow-1">
                              <div className="legend-status">{item.status}</div>
                              <div className="legend-count">{item.count} issues</div>
                            </div>
                            <div className="legend-percentage">
                              {((item.count / issuesStatusDistribution.reduce((sum, i) => sum + i.count, 0)) * 100).toFixed(1)}%
                            </div>
                          </div>
                          <div className="progress" style={{ height: '6px' }}>
                            <div 
                              className="progress-bar" 
                              style={{ 
                                width: `${(item.count / issuesStatusDistribution.reduce((sum, i) => sum + i.count, 0)) * 100}%`,
                                backgroundColor: item.color
                              }}
                            ></div>
                          </div>
                        </div>
                      </Col>
                    ))}
                  </Row>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row>
        {/* Main Content - Full Width (12 columns) */}
        <Col lg={12}>
          
          {/* SECTION 1: Assigned to Me (Unresolved) */}
          <Card className="c-card-1 mb-4">
            <Card.Header className="c-card-header-1 d-flex justify-content-between align-items-center">
              <span>Assigned to Me (Unresolved)</span>
              <span className="badge bg-primary">{assignedToMe.length}</span>
            </Card.Header>
            <Card.Body className="c-card-body-1 p-0">
              <Table hover className="c-table mb-0">
                <thead>
                  <tr>
                    <th width="100px">ID</th>
                    <th>Title</th>
                    <th width="180px">Project</th>
                    <th width="100px">Priority</th>
                    <th width="100px">Severity</th>
                    <th width="120px">Status</th>
                    <th width="100px" className="text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {assignedToMe.map(bug => (
                    <tr key={bug.id}>
                      <td>
                        <a
                          href={`https://mantis.company.com/view.php?id=${bug.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-decoration-none"
                        >
                          {bug.id} <i className="fas fa-external-link-alt ms-1 small"></i>
                        </a>
                      </td>
                      <td>{bug.title}</td>
                      <td><span className="text-muted small">{bug.project}</span></td>
                      <td><span className={getPriorityColor(bug.priority)}>{bug.priority}</span></td>
                      <td><span className={getPriorityColor(bug.severity)}>{bug.severity}</span></td>
                      <td><span className={getStatusColor(bug.status)}>{bug.status}</span></td>
                      <td className="text-center">
                        <a
                          href={`https://mantis.company.com/view.php?id=${bug.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-sm btn-outline-primary"
                        >
                          View
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>

          {/* SECTION 2: Unassigned */}
          <Card className="c-card-1 mb-4">
            <Card.Header className="c-card-header-1 d-flex justify-content-between align-items-center">
              <span>Unassigned</span>
              <span className="badge bg-warning">{unassigned.length}</span>
            </Card.Header>
            <Card.Body className="c-card-body-1 p-0">
              <Table hover className="c-table mb-0">
                <thead>
                  <tr>
                    <th width="100px">ID</th>
                    <th>Title</th>
                    <th width="180px">Project</th>
                    <th width="100px">Priority</th>
                    <th width="100px">Severity</th>
                    <th width="120px">Status</th>
                    <th width="100px" className="text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {unassigned.map(bug => (
                    <tr key={bug.id}>
                      <td>
                        <a
                          href={`https://mantis.company.com/view.php?id=${bug.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-decoration-none"
                        >
                          {bug.id} <i className="fas fa-external-link-alt ms-1 small"></i>
                        </a>
                      </td>
                      <td>{bug.title}</td>
                      <td><span className="text-muted small">{bug.project}</span></td>
                      <td><span className={getPriorityColor(bug.priority)}>{bug.priority}</span></td>
                      <td><span className={getPriorityColor(bug.severity)}>{bug.severity}</span></td>
                      <td><span className={getStatusColor(bug.status)}>{bug.status}</span></td>
                      <td className="text-center">
                        <a
                          href={`https://mantis.company.com/view.php?id=${bug.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-sm btn-outline-primary"
                        >
                          View
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>

          {/* SECTION 3: Reported by Me */}
          <Card className="c-card-1 mb-4">
            <Card.Header className="c-card-header-1 d-flex justify-content-between align-items-center">
              <span>Reported by Me</span>
              <span className="badge bg-info">{reportedByMe.length}</span>
            </Card.Header>
            <Card.Body className="c-card-body-1 p-0">
              <Table hover className="c-table mb-0">
                <thead>
                  <tr>
                    <th width="100px">ID</th>
                    <th>Title</th>
                    <th width="180px">Project</th>
                    <th width="120px">Status</th>
                    <th width="150px">Last Updated</th>
                    <th width="100px" className="text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {reportedByMe.map(bug => (
                    <tr key={bug.id}>
                      <td>
                        <a
                          href={`https://mantis.company.com/view.php?id=${bug.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-decoration-none"
                        >
                          {bug.id} <i className="fas fa-external-link-alt ms-1 small"></i>
                        </a>
                      </td>
                      <td>{bug.title}</td>
                      <td><span className="text-muted small">{bug.project}</span></td>
                      <td><span className={getStatusColor(bug.status)}>{bug.status}</span></td>
                      <td><span className="text-muted small">{bug.lastUpdated}</span></td>
                      <td className="text-center">
                        <a
                          href={`https://mantis.company.com/view.php?id=${bug.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-sm btn-outline-primary"
                        >
                          View
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>

          {/* SECTION 4: Resolved */}
          <Card className="c-card-1 mb-4">
            <Card.Header className="c-card-header-1 d-flex justify-content-between align-items-center">
              <span>Resolved</span>
              <span className="badge bg-success">{resolved.length}</span>
            </Card.Header>
            <Card.Body className="c-card-body-1 p-0">
              <Table hover className="c-table mb-0">
                <thead>
                  <tr>
                    <th width="100px">ID</th>
                    <th>Title</th>
                    <th width="120px">Resolution</th>
                    <th width="150px">Resolved By</th>
                    <th width="120px">Resolved Date</th>
                    <th width="100px" className="text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {resolved.map(bug => (
                    <tr key={bug.id}>
                      <td>
                        <a
                          href={`https://mantis.company.com/view.php?id=${bug.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-decoration-none"
                        >
                          {bug.id} <i className="fas fa-external-link-alt ms-1 small"></i>
                        </a>
                      </td>
                      <td>{bug.title}</td>
                      <td><span className="text-success">{bug.resolution}</span></td>
                      <td><span className="text-muted small">{bug.resolvedBy}</span></td>
                      <td><span className="text-muted small">{bug.resolvedDate}</span></td>
                      <td className="text-center">
                        <a
                          href={`https://mantis.company.com/view.php?id=${bug.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-sm btn-outline-primary"
                        >
                          View
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>

          {/* SECTION 5: Recently Modified (Last 30 Days) */}
          <Card className="c-card-1 mb-4">
            <Card.Header className="c-card-header-1 d-flex justify-content-between align-items-center">
              <span>Recently Modified (Last 30 Days)</span>
              <span className="badge bg-secondary">{recentlyModified.length}</span>
            </Card.Header>
            <Card.Body className="c-card-body-1 p-0">
              <Table hover className="c-table mb-0">
                <thead>
                  <tr>
                    <th width="100px">ID</th>
                    <th>Change Summary</th>
                    <th width="150px">Updated By</th>
                    <th width="140px">Date & Time</th>
                    <th width="100px" className="text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {recentlyModified.map(item => (
                    <tr key={item.id}>
                      <td>
                        <a
                          href={`https://mantis.company.com/view.php?id=${item.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-decoration-none"
                        >
                          {item.id} <i className="fas fa-external-link-alt ms-1 small"></i>
                        </a>
                      </td>
                      <td>{item.changeSummary}</td>
                      <td><span className="text-muted small">{item.updatedBy}</span></td>
                      <td><span className="text-muted small">{item.date} {item.time}</span></td>
                      <td className="text-center">
                        <a
                          href={`https://mantis.company.com/view.php?id=${item.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-sm btn-outline-primary"
                        >
                          View
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>

          {/* SECTION 6: Monitored by Me */}
          <Card className="c-card-1 mb-4">
            <Card.Header className="c-card-header-1 d-flex justify-content-between align-items-center">
              <span>Monitored by Me</span>
              <span className="badge bg-dark">{monitored.length}</span>
            </Card.Header>
            <Card.Body className="c-card-body-1 p-0">
              <Table hover className="c-table mb-0">
                <thead>
                  <tr>
                    <th width="100px">ID</th>
                    <th>Title</th>
                    <th width="180px">Project</th>
                    <th width="120px">Status</th>
                    <th width="100px" className="text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {monitored.map(bug => (
                    <tr key={bug.id}>
                      <td>
                        <a
                          href={`https://mantis.company.com/view.php?id=${bug.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-decoration-none"
                        >
                          {bug.id} <i className="fas fa-external-link-alt ms-1 small"></i>
                        </a>
                      </td>
                      <td>{bug.title}</td>
                      <td><span className="text-muted small">{bug.project}</span></td>
                      <td><span className={getStatusColor(bug.status)}>{bug.status}</span></td>
                      <td className="text-center">
                        <a
                          href={`https://mantis.company.com/view.php?id=${bug.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-sm btn-outline-primary"
                        >
                          View
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>

        </Col>
      </Row>

      {/* Activity Offcanvas */}
      <Offcanvas 
        show={showActivityOffcanvas} 
        onHide={() => setShowActivityOffcanvas(false)} 
        placement="end" 
        className="md-offcanvas"
      >
        <Offcanvas.Header className='px-4 py-3' closeButton>
          <Offcanvas.Title className='display-1'>QC Activity Timeline</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body className="p-0">
          {/* Activities Content */}
          <div className="px-4 pb-3 tab-content-scroll">
            {sortActivityData(timeline).map((activity, index) => (
              <div key={index} className='border p-3 rounded-3 mb-3'>
                <div className='p-tag fs-12'>
                  {activity.time}
                </div>
                <div className='p-tag fw-bold border-bottom pb-2'>{activity.user}</div>
                <div className='p-tag mt-2'>
                  {activity.action}
                </div>
                <div className='p-tag mt-2'>
                  <a
                    href={`https://mantis.company.com/view.php?id=${activity.issue}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-decoration-none"
                  >
                    {activity.issue} <i className="fas fa-external-link-alt ms-1 small"></i>
                  </a>
                </div>
              </div>
            ))}
            <div className="text-center mt-3 pt-3 border-top">
              <a
                href="https://mantis.company.com/view_all_bug_page.php"
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-sm btn-outline-primary"
              >
                <i className="fas fa-external-link-alt me-1"></i>
                View All in Mantis
              </a>
            </div>
          </div>
        </Offcanvas.Body>
      </Offcanvas>
    </div>
  );
};

export default MyQCView;
